import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import CompressIcon from '@mui/icons-material/Compress'
import type { ChatContextUsage, ChatUsage } from 'chat-shared'

const formatTokens = (value: number): string => value.toLocaleString('de-DE')

const DONUT_SIZE = 20
const DONUT_STROKE = 4

/** Kleiner Donut, der den Kontext-Füllstand (0–100 %) anzeigt. */
const ContextDonut = ({ percent }: { percent: number }) => {
  const radius = (DONUT_SIZE - DONUT_STROKE) / 2
  const circumference = 2 * Math.PI * radius
  return (
    <svg
      width={DONUT_SIZE}
      height={DONUT_SIZE}
      // Start der Füllung oben statt rechts (SVG-Kreise beginnen bei 3 Uhr).
      style={{ transform: 'rotate(-90deg)' }}
      role="img"
      aria-label={`Kontext zu ${percent} % gefüllt`}
    >
      <circle
        cx={DONUT_SIZE / 2}
        cy={DONUT_SIZE / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={DONUT_STROKE}
        opacity={0.25}
      />
      <circle
        cx={DONUT_SIZE / 2}
        cy={DONUT_SIZE / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={DONUT_STROKE}
        strokeDasharray={`${(percent / 100) * circumference} ${circumference}`}
      />
    </svg>
  )
}

/**
 * Kontext-Indikator neben der Chat-Eingabe (wie bei claude.ai): Donut mit
 * Füllstand des Kontextfensters, Details im Tooltip und ein Button, der den
 * Kontext serverseitig komprimiert (LLM-Zusammenfassung).
 */
export const ContextUsageIndicator = ({
  usage,
  lastUsage,
  onCompact,
  isCompacting,
  canCompact,
}: {
  usage?: ChatContextUsage
  lastUsage?: ChatUsage
  onCompact: () => void
  isCompacting: boolean
  /** false, solange es nichts zu komprimieren gibt oder gerade gestreamt wird. */
  canCompact: boolean
}) => {
  if (!usage || usage.budgetTokens <= 0) return null

  const percent = Math.min(100, Math.round((usage.estimatedTokens / usage.budgetTokens) * 100))
  const color = percent >= 95 ? 'error.main' : percent >= 80 ? 'warning.main' : 'text.secondary'

  const details = (
    <>
      <Typography variant="caption" display="block">
        Kontext: ~{formatTokens(usage.estimatedTokens)} von {formatTokens(usage.budgetTokens)}{' '}
        Tokens ({percent} %)
      </Typography>
      {lastUsage && (
        <Typography variant="caption" display="block">
          Letzte Antwort: {formatTokens(lastUsage.promptTokens)} Eingabe +{' '}
          {formatTokens(lastUsage.completionTokens)} Ausgabe
        </Typography>
      )}
      <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
        Über dem Budget werden ältere Nachrichten automatisch gekürzt.
      </Typography>
    </>
  )

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Tooltip title={details}>
        <Box display="flex" alignItems="center" gap={0.75} sx={{ color, cursor: 'default' }}>
          <ContextDonut percent={percent} />
          <Typography variant="caption" sx={{ color }}>
            {percent} %
          </Typography>
        </Box>
      </Tooltip>
      <Tooltip title="Kontext komprimieren: fasst den bisherigen Verlauf zusammen, damit wieder Platz im Kontextfenster ist">
        {/* span, damit der Tooltip auch am deaktivierten Button funktioniert */}
        <span>
          <IconButton
            size="small"
            aria-label="Kontext komprimieren"
            onClick={onCompact}
            disabled={!canCompact || isCompacting}
          >
            {isCompacting ? <CircularProgress size={16} /> : <CompressIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}
